import {Fragment, useContext, useRef, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {WebsiteContext} from "../../../index";
import Currency from "@autocx/forms/src/controls/currency";
import BidCountdown from "../../product-card/components/bid-countdown";
import Confetti from "react-dom-confetti";
import classNames from "classnames";
import axios from "axios";
import {AUCTIONSTATUSENUM} from "../index";
import Spinner from "../../../components/spinner";
import BidHoldRecovery from "./bid-hold-recovery";
import ModalContentTransition from "../../../components/modal-content-transition";
import confettiConfig from "./confetti-config";

function BidButton({saleMethod, usersBid, openModal, className, disabled, label}) {
    const context = useContext(WebsiteContext);
    const isTender = saleMethod?.toLowerCase() === 'tender';
    const ownAmountLabel = isTender ? 'Your offer' : 'Your bid';

    const buttonText = label || (usersBid?.amount !== null && usersBid?.amount !== undefined
        ? <>
            {ownAmountLabel}: <Currency.DisplayValue currency={context.currency}
                                                      country={context.country}
                                                      value={usersBid?.amount}
                                                      emptyPlaceholder=""/>
        </>
        : isTender
            ? 'Make Offer'
            : 'Place Bid');

    return (
        <button
            data-action=""
            disabled={disabled}
            className={classNames("button mt-1 w-full justify-center", className)}
            onClick={openModal}
            type={"button"}
        >
            {buttonText}
        </button>
    );
}

const createBidClientReference = () => (
    typeof window !== "undefined" && window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`
);

function BidModal({
    isOpen,
    closeModal,
    salesEvent,
    setStatus,
    onBidSuccess,
    now,
    usersBid,
    totalBids
}) {
    const context = useContext(WebsiteContext)
    const [amount, setAmount] = useState(usersBid?.amount);
    const [errorMessage, setErrorMessage] = useState("")
    const [minimumNextBidAmount, setMinimumNextBidAmount] = useState(null)
    const [reserveMet, setReserveMet] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [holdRecoveryCode, setHoldRecoveryCode] = useState(null)
    const saleMethod = salesEvent?.sellingMethod?.toLowerCase();
    const isTender = saleMethod === 'tender';
    const cancelButtonRef = useRef(null)
    const bidClientReference = useRef(createBidClientReference())
    const showHoldRecovery = !!holdRecoveryCode;
    const title = reserveMet
        ? 'Congratulations!'
        : showHoldRecovery
            ? holdRecoveryCode === 'replacement_required'
                ? 'Update Your Card Hold'
                : 'Secure Your Bid'
            : saleMethod === 'tender' ? 'Make Offer' : 'Place Bid';
    const modalStep = reserveMet ? 'success' : showHoldRecovery ? 'hold-recovery' : 'bid-entry';
    const buttonLabel = saleMethod === 'tender' ? 'Submit Offer' : 'Place Bid';
    const inputLabel = saleMethod === 'tender' ? 'Enter offer amount' : 'Enter bid amount';

    const applyBidResponse = data => {
        const response = typeof data === 'string' ? {result: data} : (data || {});
        const result = response?.result;

        // Auction: treat accepted/aboveReserve/belowReserve as a successful bid placement.
        if (result === 'accepted' || result === 'aboveReserve' || result === 'belowReserve') {
            const bidUpdate = {
                amount,
                expectedTotalBids: saleMethod === 'auction'
                    ? (response?.totalBids ?? ((totalBids ?? salesEvent?.totalBids ?? 0) + 1))
                    : undefined
            };
            if (result === 'aboveReserve') bidUpdate.reserveMet = true;
            if (response?.endDate || response?.secondsRemaining !== undefined) {
                bidUpdate.salesEvent = {
                    publicStateVersion: response?.publicStateVersion,
                    endDate: response?.endDate,
                    secondsRemaining: response?.secondsRemaining,
                    highestBid: response?.highestBid,
                    totalBids: response?.totalBids,
                    reserveMet: response?.reserveMet,
                    lateBidExtensionSeconds: response?.lateBidExtensionSeconds
                };
            }
            onBidSuccess(bidUpdate)
            closeModal();
            return;
        }

        // Tender: keep existing instant-win UX.
        if (saleMethod === 'tender') {
            if (result === 'instantWin') {
                onBidSuccess({amount, isWinner: true});
                setReserveMet(true);
            } else if (result === 'biddingClosed') {
                setStatus(AUCTIONSTATUSENUM.CLOSED);
            }
        } else if (result === 'biddingClosed') {
            setStatus(AUCTIONSTATUSENUM.CLOSED);
        }
    };

    const submitBid = ({afterHold = false} = {}) => {
        if (isSubmitting) return Promise.resolve();
        setIsSubmitting(true);
        // Submit logic for offer or bid
        return axios
            .post(`/api/v2/salesevents/lots/${salesEvent.lotId}/bids`, {
                amount,
                clientReference: bidClientReference.current
            })
            .then(({data}) => {
                applyBidResponse(data);
            })
            .catch(err => {
                const errorCode = err?.response?.data?.code || err?.response?.data;
                const nextBidAmount = err?.response?.data?.minimumNextBidAmount;
                const suggestedBidAmount = err?.response?.data?.suggestedBidAmount ?? nextBidAmount;
                const bidIncrementAmount = err?.response?.data?.bidIncrementAmount;
                const responseMessage = err?.response?.data?.message;

                if (afterHold) setHoldRecoveryCode(null);
                setMinimumNextBidAmount(Number.isFinite(suggestedBidAmount) ? suggestedBidAmount : null);

                if (err?.response?.status === 403 && errorCode === 'customer_type_not_allowed') {
                    setErrorMessage(responseMessage ||
                        (isTender
                            ? "Your account is not approved for this tender. Contact the sales team if you think you should have access."
                            : "This auction is limited to selected Customer Types, and your account is not currently eligible. Contact the auction team if you think you should have access."));
                } else if (err?.response?.status === 401 || err?.response?.status === 403) {
                    setErrorMessage(saleMethod === 'tender'
                        ? "Please sign in to submit an offer"
                        : "Please sign in to place a bid")
                } else if (err?.response?.status === 409 && errorCode === 'hold_required') {
                    setMinimumNextBidAmount(null);
                    if (afterHold) {
                        setErrorMessage("Your card hold was authorised, but this bid still needs review. No additional hold is needed. Please contact the auction team.")
                    } else {
                        setErrorMessage("")
                        setHoldRecoveryCode(errorCode)
                    }
                } else if (err?.response?.status === 409 && errorCode === 'replacement_required') {
                    setMinimumNextBidAmount(null);
                    if (afterHold) {
                        setErrorMessage("Your card hold was authorised, but this bid still needs review. No additional hold is needed. Please contact the auction team.")
                    } else {
                        setErrorMessage("")
                        setHoldRecoveryCode(errorCode)
                    }
                } else if (err?.response?.status === 409 && errorCode === 'bid_state_changed') {
                    setMinimumNextBidAmount(null);
                    setErrorMessage(responseMessage || "The auction changed while this bid was being checked. Please try again.")
                } else if (err?.response?.status === 400 && errorCode === 'bidCantBeLowered') {
                    setMinimumNextBidAmount(null);
                    setErrorMessage("You are unable to make an offer lower than your previous one")
                } else if (err?.response?.status === 400 && errorCode === 'bidIncrementInvalid') {
                    setErrorMessage(bidIncrementAmount
                        ? <>Enter a bid in <Currency.DisplayValue
                            currency={context.currency}
                            country={context.country}
                            value={bidIncrementAmount}
                            emptyPlaceholder=""
                        /> steps</>
                        : "Enter a valid bid amount")
                } else if (err?.response?.status === 400 && (errorCode === 'bidNotHighEnough' || errorCode === 'bidTooLow')) {
                    setErrorMessage("That bid is too low")
                } else {
                    setMinimumNextBidAmount(null);
                    setErrorMessage(saleMethod === 'tender'
                        ? "There was a problem submitting your offer - please try again"
                        : "There was a problem submitting your bid - please try again")
                }
            })
            .finally(() => {
                setIsSubmitting(false);
            })

    };

    const handleSubmit = (event) => {
        event.preventDefault();
        submitBid();
    };

    const retryBidAfterHold = () => {
        setErrorMessage("");
        return submitBid({afterHold: true});
    };

    const handleChange = (value) => {
        setAmount(value)
        setErrorMessage("")
        setMinimumNextBidAmount(null)
        bidClientReference.current = createBidClientReference()
    }

    const handleClose = () => {
        closeModal()
    }

    const useMinimumNextBidAmount = () => {
        if (minimumNextBidAmount == null) return;
        setAmount(minimumNextBidAmount);
        bidClientReference.current = createBidClientReference();
        setErrorMessage("");
        setMinimumNextBidAmount(null);
    };

    const resetState = () => {
        setAmount(usersBid?.amount);
        setErrorMessage("");
        setMinimumNextBidAmount(null);
        setIsSubmitting(false);
        setHoldRecoveryCode(null);
        bidClientReference.current = createBidClientReference();
    };

    return (
        <Transition appear show={isOpen} as={Fragment} afterLeave={resetState}>
            {/*Set initial focus to Cancel button so disclaimer doesn't get hidden on mobile by keyboard*/}
            <Dialog as="div" className="relative z-10" onClose={handleClose} initialFocus={cancelButtonRef}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30"/>
                </Transition.Child>
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel
                                className="relative bg-white rounded-lg p-6 w-full max-w-md mx-auto text-center flex flex-col items-center shadow-xl"
                                data-popup=""
                            >
                                <ModalContentTransition transitionKey={modalStep}>
                                    <Dialog.Title as="h3" className="!text-2xl !mb-0 font-semibold text-gray-900"
                                                  data-title="">
                                        {title}
                                    </Dialog.Title>
                                    {!reserveMet && showHoldRecovery ? (
                                        <BidHoldRecovery
                                            lotId={salesEvent.lotId}
                                            onReadyToRetry={retryBidAfterHold}
                                            onBack={() => setHoldRecoveryCode(null)}
                                        />
                                    ) : !reserveMet ?
                                    <form className="mt-2 w-full" onSubmit={handleSubmit} data-body="">
                                        <BidCountdown salesEvent={salesEvent}
                                                      date={now + salesEvent.secondsRemaining * 1000 + 300}
                                                      warningMinutes={1}/>
                                        <label htmlFor="amount"
                                               className="text-sm font-medium text-gray-700 hidden">
                                            {inputLabel}
                                        </label>
                                        <Currency id="amount"
                                                  className="block w-full px-2 py-1 text-lg font-medium text-center border-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
                                                  placeholder={inputLabel}
                                                  hidePrefix={true}
                                                  value={amount}
                                                  min="1"
                                                  currency={context.currency} name={'amount'}
                                                  onChange={(e, name, value) => handleChange(value)} required={true}/>

                                        {salesEvent.feeDescription && !errorMessage &&
                                            <p className={"text-left my-2 text-gray-700 text-xs whitespace-pre-line"}
                                               dangerouslySetInnerHTML={{__html: salesEvent.feeDescription}}/>
                                        }
                                        {errorMessage &&
                                            <div
                                                className={"text-red-500 font-semibold leading-tight text-sm text-center animate__animated animate__headShake mt-2"}
                                                data-error="">
                                                {errorMessage}
                                            </div>
                                        }
                                        {minimumNextBidAmount != null &&
                                            <button
                                                type="button"
                                                onClick={useMinimumNextBidAmount}
                                                className="mt-2 text-sm font-semibold text-link"
                                            >
                                                Use <Currency.DisplayValue
                                                    currency={context.currency}
                                                    country={context.country}
                                                    value={minimumNextBidAmount}
                                                    emptyPlaceholder=""
                                                />
                                            </button>
                                        }
                                        <div className="mt-4 text-center">
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                aria-busy={isSubmitting}
                                                className={classNames("button mt-1 w-full justify-center relative", isSubmitting ? "pointer-events-none" : null)}>
                                                <span className={classNames("transition-opacity", isSubmitting ? "opacity-0" : "opacity-100")}>
                                                    {buttonLabel}
                                                </span>
                                                {isSubmitting ? (
                                                    <Spinner
                                                        containerClassName="absolute inset-0 my-0"
                                                        className="h-5 w-5"
                                                    />
                                                ) : null}
                                            </button>
                                            <button ref={cancelButtonRef}
                                                    onClick={handleClose}
                                                    type={"button"}
                                                    className={"text-link font-semibold mt-2 text-sm"}>Cancel
                                            </button>
                                        </div>
                                    </form>
                                    :
                                    <p className="mt-2" data-body="">
                                        Your offer of <Currency.DisplayValue
                                        country={context.country} currency={context.currency} value={amount}
                                        emptyPlaceholder={""}/> has hit
                                        the reserve. <br/>You'll
                                        receive an email soon with all the details.
                                        <button type="button" onClick={() => {
                                            setStatus(AUCTIONSTATUSENUM.CLOSED);
                                            closeModal()
                                        }}
                                                className={"text-link font-medium text-base mt-4"}>Okay
                                        </button>
                                    </p>
                                    }
                                </ModalContentTransition>
                                <Confetti active={reserveMet} config={confettiConfig}/>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}

BidModal.BidButton = BidButton;

export default BidModal;
